import { IEntityInfo, NSARouterMakerResult } from "namirasoft-account-react";
import { ProductUUID } from "namirasoft-api-product";
import { FilterItem, IStorageLocal, SortItem } from "namirasoft-core";
import { AccountInputRow, AccountRow, BranchInputRow, BranchRow, CompanyInputRow, CompanyRow, CurrencyExchangeInputRow, CurrencyExchangeRow, CurrencyInputRow, CurrencyRow, FiscalYearInputRow, FiscalYearRow, FuelInputRow, FuelRow, GifiRow, JournalEntryInputRow, JournalEntryRow, JournalEntryType, LockInputRow, LockRow, MaintenancePlanInputRow, MaintenancePlanRow, NamirasoftERPMetaDatabase, OdometerInputRow, OdometerRow, PartyInputRow, PartyRow, PartyTypeInputRow, PartyTypeRow, RefuelingInputRow, RefuelingRow, ServiceInputRow, ServiceRow, ServiceTypeInputRow, ServiceTypeRow, TagGroupInputRow, TagGroupRow, TagInputRow, TagRow, VehicleInputRow, VehicleRow } from "namirasoft-erp";
import { NamirasoftMap } from "namirasoft-site-map";
import { BaseColumnFormatter, BooleanFormatter, DateFormatter, DateTimeFormatter, EnumFormatter, FloatFormatter, ForeColorFormatter, IDFormatter, IntegerFormatter, StringFormatter, StringFormatterSizeType } from "namirasoft-site-react";
import { ErpRouterProps } from "./ErpRouterProps";
import { HiddenWhenZeroFormatter } from "./formatters/HiddenWhenZeroFormatter";
import { ErpRoutes } from "./ErpRoutes";
import { ErpScope } from "./ErpScope";
import { FiscalYearMenus } from "./FiscalYearMenus";
import { JournalEntryMenus } from "./JournalEntryMenus";

type ListFn<T> = (filters: FilterItem[] | null, page: number | null, size: number | null, sorts: SortItem[], all_workspaces: boolean | null) => Promise<{ count: number; rows: T[] }>;

function scopedList<T>(table: { columns: { [k: string]: any } }, list: ListFn<T>, scopeKeys: ("company_id" | "branch_id" | "fiscal_year_id")[]): ListFn<T>
{
    return (filters, page, size, sorts) =>
    {
        let f = filters;
        for (let key of scopeKeys)
        {
            let id = key === "company_id" ? ErpScope.getCompanyIdRaw()
                : key === "branch_id" ? ErpScope.getBranchIdRaw()
                    : ErpScope.getFiscalYearIdRaw();
            if (id)
                f = ErpScope.pushFilter(f, table, key, id);
        }
        return list(f, page, size, sorts, null);
    };
}

export class ErpEntities
{
    static currency(props: NSARouterMakerResult & ErpRouterProps): IEntityInfo<CurrencyRow, CurrencyInputRow>
    {
        let db = new NamirasoftERPMetaDatabase(props.account.token_manager, console.error);
        let namirasoft = new NamirasoftMap(props.account.token_manager, console.error);

        return {
            product_id: ProductUUID.NAMIRASOFT_ERP,
            name: "currency",
            tables: [{ table: db.tables.currency, required: false }],
            client: {
                getColumns: () => [
                    BaseColumnFormatter.getColumn(db.tables.currency.columns.id, new IDFormatter(namirasoft, null)),
                    BaseColumnFormatter.getColumn(db.tables.currency.columns.name, new StringFormatter(StringFormatterSizeType.TwoWords)),
                    BaseColumnFormatter.getColumn(db.tables.currency.columns.fraction_name, new StringFormatter(StringFormatterSizeType.Word)),
                    BaseColumnFormatter.getColumn(db.tables.currency.columns.fraction_value, new IntegerFormatter(StringFormatterSizeType.Word)),
                    BaseColumnFormatter.getColumn(db.tables.currency.columns.symbol, new StringFormatter(StringFormatterSizeType.Word)),
                    BaseColumnFormatter.getColumn(db.tables.currency.columns.enabled, new ForeColorFormatter(new BooleanFormatter("Yes", "No"), { true: "green", false: "red" })),
                    BaseColumnFormatter.getColumn(db.tables.currency.columns.description, new StringFormatter(StringFormatterSizeType.Description)),
                    BaseColumnFormatter.getColumn(db.tables.currency.columns.created_at, new DateTimeFormatter()),
                    BaseColumnFormatter.getColumn(db.tables.currency.columns.updated_at, new DateTimeFormatter()),
                ],
                getListURL: () => ErpRoutes.CurrencyList,
                getNewURL: () => ErpRoutes.CurrencyNew,
                getViewURL: (id) => ErpRoutes.CurrencyView(id),
                getEditURL: (id) => ErpRoutes.CurrencyEdit(id),
                getCopyURL: (id) => ErpRoutes.CurrencyCopy(id),
            },
            server: {
                value_list: props.servers.erp.value.List,
                list: props.servers.erp.currency.List,
                get: props.servers.erp.currency.Get,
                create: props.servers.erp.currency.Create,
                update: props.servers.erp.currency.Update,
                delete: props.servers.erp.currency.Delete,
            }
        }
    }

    static currency_exchange(props: NSARouterMakerResult & ErpRouterProps): IEntityInfo<CurrencyExchangeRow, CurrencyExchangeInputRow>
    {
        let db = new NamirasoftERPMetaDatabase(props.account.token_manager, console.error);
        let namirasoft = new NamirasoftMap(props.account.token_manager, console.error);

        return {
            product_id: ProductUUID.NAMIRASOFT_ERP,
            name: "currency_exchange",
            tables: [{ table: db.tables.currency_exchange, required: false }],
            client: {
                getColumns: () => [
                    BaseColumnFormatter.getColumn(db.tables.currency_exchange.columns.id, new IDFormatter(namirasoft, null)),
                    BaseColumnFormatter.getColumn(db.tables.currency_exchange.columns.date_time, new DateTimeFormatter()),
                    BaseColumnFormatter.getColumn(db.tables.currency_exchange.columns.from_currency_id, new IDFormatter(namirasoft, new IStorageLocal())),
                    BaseColumnFormatter.getColumn(db.tables.currency_exchange.columns.to_currency_id, new IDFormatter(namirasoft, new IStorageLocal())),
                    BaseColumnFormatter.getColumn(db.tables.currency_exchange.columns.rate_num, new FloatFormatter()),
                    BaseColumnFormatter.getColumn(db.tables.currency_exchange.columns.rate_den, new FloatFormatter()),
                    BaseColumnFormatter.getColumn(db.tables.currency_exchange.columns.created_at, new DateTimeFormatter()),
                    BaseColumnFormatter.getColumn(db.tables.currency_exchange.columns.updated_at, new DateTimeFormatter()),
                ],
                getListURL: () => ErpRoutes.CurrencyExchangeList,
                getNewURL: () => ErpRoutes.CurrencyExchangeNew,
                getViewURL: (id) => ErpRoutes.CurrencyExchangeView(id),
                getEditURL: (id) => ErpRoutes.CurrencyExchangeEdit(id),
                getCopyURL: (id) => ErpRoutes.CurrencyExchangeCopy(id),
            },
            server: {
                value_list: props.servers.erp.value.List,
                list: props.servers.erp.currency_exchange.List,
                get: props.servers.erp.currency_exchange.Get,
                create: props.servers.erp.currency_exchange.Create,
                update: props.servers.erp.currency_exchange.Update,
                delete: props.servers.erp.currency_exchange.Delete,
            }
        }
    }

    static company(props: NSARouterMakerResult & ErpRouterProps): IEntityInfo<CompanyRow, CompanyInputRow>
    {
        let db = new NamirasoftERPMetaDatabase(props.account.token_manager, console.error);
        let namirasoft = new NamirasoftMap(props.account.token_manager, console.error);

        return {
            product_id: ProductUUID.NAMIRASOFT_ERP,
            name: "company",
            tables: [{ table: db.tables.company, required: false }],
            client: {
                getColumns: () => [
                    BaseColumnFormatter.getColumn(db.tables.company.columns.id, new IDFormatter(namirasoft, null)),
                    BaseColumnFormatter.getColumn(db.tables.company.columns.name, new StringFormatter()),
                    BaseColumnFormatter.getColumn(db.tables.company.columns.enabled, new ForeColorFormatter(new BooleanFormatter("Yes", "No"), { true: "green", false: "red" })),
                    BaseColumnFormatter.getColumn(db.tables.company.columns.current_year_pl_account_id, new IDFormatter(namirasoft, new IStorageLocal())),
                    BaseColumnFormatter.getColumn(db.tables.company.columns.retained_earnings_account_id, new IDFormatter(namirasoft, new IStorageLocal())),
                    BaseColumnFormatter.getColumn(db.tables.company.columns.description, new StringFormatter(StringFormatterSizeType.Description)),
                    BaseColumnFormatter.getColumn(db.tables.company.columns.created_at, new DateTimeFormatter()),
                    BaseColumnFormatter.getColumn(db.tables.company.columns.updated_at, new DateTimeFormatter()),
                ],
                getListURL: () => ErpRoutes.CompanyList,
                getNewURL: () => ErpRoutes.CompanyNew,
                getViewURL: (id) => ErpRoutes.CompanyView(id),
                getEditURL: (id) => ErpRoutes.CompanyEdit(id),
                getCopyURL: (id) => ErpRoutes.CompanyCopy(id),
            },
            server: {
                value_list: props.servers.erp.value.List,
                list: props.servers.erp.company.List,
                get: props.servers.erp.company.Get,
                create: props.servers.erp.company.Create,
                update: props.servers.erp.company.Update,
                delete: props.servers.erp.company.Delete,
            }
        }
    }

    static branch(props: NSARouterMakerResult & ErpRouterProps): IEntityInfo<BranchRow, BranchInputRow>
    {
        let db = new NamirasoftERPMetaDatabase(props.account.token_manager, console.error);
        let namirasoft = new NamirasoftMap(props.account.token_manager, console.error);

        return {
            product_id: ProductUUID.NAMIRASOFT_ERP,
            name: "branch",
            tables: [{ table: db.tables.branch, required: false }],
            client: {
                getColumns: () => [
                    BaseColumnFormatter.getColumn(db.tables.branch.columns.id, new IDFormatter(namirasoft, null)),
                    BaseColumnFormatter.getColumn(db.tables.branch.columns.company_id, new IDFormatter(namirasoft, new IStorageLocal())),
                    BaseColumnFormatter.getColumn(db.tables.branch.columns.default_currency_id, new IDFormatter(namirasoft, new IStorageLocal())),
                    BaseColumnFormatter.getColumn(db.tables.branch.columns.name, new StringFormatter()),
                    BaseColumnFormatter.getColumn(db.tables.branch.columns.country, new StringFormatter()),
                    BaseColumnFormatter.getColumn(db.tables.branch.columns.enabled, new ForeColorFormatter(new BooleanFormatter("Yes", "No"), { true: "green", false: "red" })),
                    BaseColumnFormatter.getColumn(db.tables.branch.columns.description, new StringFormatter(StringFormatterSizeType.Description)),
                    BaseColumnFormatter.getColumn(db.tables.branch.columns.created_at, new DateTimeFormatter()),
                    BaseColumnFormatter.getColumn(db.tables.branch.columns.updated_at, new DateTimeFormatter()),
                ],
                getListURL: () => ErpRoutes.BranchList,
                getNewURL: () => ErpRoutes.BranchNew,
                getViewURL: (id) => ErpRoutes.BranchView(id),
                getEditURL: (id) => ErpRoutes.BranchEdit(id),
                getCopyURL: (id) => ErpRoutes.BranchCopy(id),
            },
            server: {
                value_list: props.servers.erp.value.List,
                list: scopedList(db.tables.branch, props.servers.erp.branch.List, ["company_id"]),
                get: props.servers.erp.branch.Get,
                create: props.servers.erp.branch.Create,
                update: props.servers.erp.branch.Update,
                delete: props.servers.erp.branch.Delete,
            }
        }
    }

    static fiscal_year(props: NSARouterMakerResult & ErpRouterProps): IEntityInfo<FiscalYearRow, FiscalYearInputRow>
    {
        let db = new NamirasoftERPMetaDatabase(props.account.token_manager, console.error);
        let namirasoft = new NamirasoftMap(props.account.token_manager, console.error);

        return {
            product_id: ProductUUID.NAMIRASOFT_ERP,
            name: "fiscal_year",
            tables: [{ table: db.tables.fiscal_year, required: false }],
            client: {
                getColumns: () => [
                    BaseColumnFormatter.getColumn(db.tables.fiscal_year.columns.id, new IDFormatter(namirasoft, null)),
                    BaseColumnFormatter.getColumn(db.tables.fiscal_year.columns.company_id, new IDFormatter(namirasoft, new IStorageLocal())),
                    BaseColumnFormatter.getColumn(db.tables.fiscal_year.columns.name, new StringFormatter()),
                    BaseColumnFormatter.getColumn(db.tables.fiscal_year.columns.date_start, new DateFormatter()),
                    BaseColumnFormatter.getColumn(db.tables.fiscal_year.columns.date_end, new DateFormatter()),
                    BaseColumnFormatter.getColumn(db.tables.fiscal_year.columns.enabled, new ForeColorFormatter(new BooleanFormatter("Yes", "No"), { true: "green", false: "red" })),
                    BaseColumnFormatter.getColumn(db.tables.fiscal_year.columns.description, new StringFormatter(StringFormatterSizeType.Description)),
                    BaseColumnFormatter.getColumn(db.tables.fiscal_year.columns.company_id, new IDFormatter(namirasoft, new IStorageLocal())),
                    BaseColumnFormatter.getColumn(db.tables.fiscal_year.columns.created_at, new DateTimeFormatter()),
                    BaseColumnFormatter.getColumn(db.tables.fiscal_year.columns.updated_at, new DateTimeFormatter()),
                ],
                getListURL: () => ErpRoutes.FiscalYearList,
                getNewURL: () => ErpRoutes.FiscalYearNew,
                getViewURL: (id) => ErpRoutes.FiscalYearView(id),
                getEditURL: (id) => ErpRoutes.FiscalYearEdit(id),
                getCopyURL: (id) => ErpRoutes.FiscalYearCopy(id),
                getMenus: (getIDs, getRows) =>
                {
                    let reload = () => window.location.reload();
                    let typedGetRows = getRows as () => { id: string; }[];
                    return {
                        "Lifecycle": [
                            FiscalYearMenus.open(props, getIDs, typedGetRows),
                            FiscalYearMenus.unopen(props, getIDs, typedGetRows, reload),
                            FiscalYearMenus.close(props, getIDs, typedGetRows),
                            FiscalYearMenus.unclose(props, getIDs, typedGetRows, reload),
                        ]
                    };
                },
            },
            server: {
                value_list: props.servers.erp.value.List,
                list: scopedList(db.tables.fiscal_year, props.servers.erp.fiscal_year.List, ["company_id"]),
                get: props.servers.erp.fiscal_year.Get,
                create: props.servers.erp.fiscal_year.Create,
                update: props.servers.erp.fiscal_year.Update,
                delete: props.servers.erp.fiscal_year.Delete,
            }
        }
    }

    static account(props: NSARouterMakerResult & ErpRouterProps): IEntityInfo<AccountRow, AccountInputRow>
    {
        let db = new NamirasoftERPMetaDatabase(props.account.token_manager, console.error);
        let namirasoft = new NamirasoftMap(props.account.token_manager, console.error);

        return {
            product_id: ProductUUID.NAMIRASOFT_ERP,
            name: "account",
            tables: [{ table: db.tables.account, required: false }],
            client: {
                getColumns: () => [
                    BaseColumnFormatter.getColumn(db.tables.account.columns.id, new IDFormatter(namirasoft, null)),
                    BaseColumnFormatter.getColumn(db.tables.account.columns.company_id, new IDFormatter(namirasoft, new IStorageLocal())),
                    BaseColumnFormatter.getColumn(db.tables.account.columns.branch_id, new IDFormatter(namirasoft, new IStorageLocal())),
                    BaseColumnFormatter.getColumn(db.tables.account.columns.account_id, new IDFormatter(namirasoft, new IStorageLocal())),
                    BaseColumnFormatter.getColumn(db.tables.account.columns.code, new StringFormatter(StringFormatterSizeType.Word)),
                    BaseColumnFormatter.getColumn(db.tables.account.columns.name, new StringFormatter(StringFormatterSizeType.FullName)),
                    BaseColumnFormatter.getColumn(db.tables.account.columns.type, new EnumFormatter()),
                    BaseColumnFormatter.getColumn(db.tables.account.columns.is_group, new ForeColorFormatter(new BooleanFormatter("Yes", "No"), { false: "green" })),
                    BaseColumnFormatter.getColumn(db.tables.account.columns.party_type_ids, new IDFormatter(namirasoft, new IStorageLocal())),
                    BaseColumnFormatter.getColumn(db.tables.account.columns.tag_group_ids, new IDFormatter(namirasoft, new IStorageLocal())),
                    BaseColumnFormatter.getColumn(db.tables.account.columns.enabled, new ForeColorFormatter(new BooleanFormatter("Yes", "No"), { true: "green", false: "red" })),
                    BaseColumnFormatter.getColumn(db.tables.account.columns.description, new StringFormatter(StringFormatterSizeType.Description)),
                    BaseColumnFormatter.getColumn(db.tables.account.columns.gifi, new StringFormatter(StringFormatterSizeType.Word), "GIFI"),
                    BaseColumnFormatter.getColumn(db.tables.account.columns.gifi_report_type, new EnumFormatter(), "GIFI"),
                    BaseColumnFormatter.getColumn(db.tables.account.columns.gifi_include_closing, new ForeColorFormatter(new BooleanFormatter("Yes", "No"), { true: "green", false: "red" }), "GIFI"),
                    BaseColumnFormatter.getColumn(db.tables.account.columns.created_at, new DateTimeFormatter()),
                    BaseColumnFormatter.getColumn(db.tables.account.columns.updated_at, new DateTimeFormatter()),
                ],
                getListURL: () => ErpRoutes.AccountList,
                getNewURL: () => ErpRoutes.AccountNew,
                getViewURL: (id) => ErpRoutes.AccountView(id),
                getEditURL: (id) => ErpRoutes.AccountEdit(id),
                getCopyURL: (id) => ErpRoutes.AccountCopy(id),
            },
            server: {
                value_list: props.servers.erp.value.List,
                list: scopedList(db.tables.account, props.servers.erp.account.List, ["company_id", "branch_id"]),
                get: props.servers.erp.account.Get,
                create: props.servers.erp.account.Create,
                update: props.servers.erp.account.Update,
                delete: props.servers.erp.account.Delete,
            }
        }
    }

    static party_type(props: NSARouterMakerResult & ErpRouterProps): IEntityInfo<PartyTypeRow, PartyTypeInputRow>
    {
        let db = new NamirasoftERPMetaDatabase(props.account.token_manager, console.error);
        let namirasoft = new NamirasoftMap(props.account.token_manager, console.error);

        return {
            product_id: ProductUUID.NAMIRASOFT_ERP,
            name: "party_type",
            tables: [{ table: db.tables.party_type, required: false }],
            client: {
                getColumns: () => [
                    BaseColumnFormatter.getColumn(db.tables.party_type.columns.id, new IDFormatter(namirasoft, null)),
                    BaseColumnFormatter.getColumn(db.tables.party_type.columns.company_id, new IDFormatter(namirasoft, new IStorageLocal())),
                    BaseColumnFormatter.getColumn(db.tables.party_type.columns.branch_id, new IDFormatter(namirasoft, new IStorageLocal())),
                    BaseColumnFormatter.getColumn(db.tables.party_type.columns.name, new StringFormatter(StringFormatterSizeType.FullName)),
                    BaseColumnFormatter.getColumn(db.tables.party_type.columns.enabled, new ForeColorFormatter(new BooleanFormatter("Yes", "No"), { true: "green", false: "red" })),
                    BaseColumnFormatter.getColumn(db.tables.party_type.columns.description, new StringFormatter(StringFormatterSizeType.Description)),
                    BaseColumnFormatter.getColumn(db.tables.party_type.columns.created_at, new DateTimeFormatter()),
                    BaseColumnFormatter.getColumn(db.tables.party_type.columns.updated_at, new DateTimeFormatter()),
                ],
                getListURL: () => ErpRoutes.PartyTypeList,
                getNewURL: () => ErpRoutes.PartyTypeNew,
                getViewURL: (id) => ErpRoutes.PartyTypeView(id),
                getEditURL: (id) => ErpRoutes.PartyTypeEdit(id),
                getCopyURL: (id) => ErpRoutes.PartyTypeCopy(id),
            },
            server: {
                value_list: props.servers.erp.value.List,
                list: scopedList(db.tables.party_type, props.servers.erp.party_type.List, ["company_id", "branch_id"]),
                get: props.servers.erp.party_type.Get,
                create: props.servers.erp.party_type.Create,
                update: props.servers.erp.party_type.Update,
                delete: props.servers.erp.party_type.Delete,
            }
        }
    }

    static party(props: NSARouterMakerResult & ErpRouterProps): IEntityInfo<PartyRow, PartyInputRow>
    {
        let db = new NamirasoftERPMetaDatabase(props.account.token_manager, console.error);
        let namirasoft = new NamirasoftMap(props.account.token_manager, console.error);

        return {
            product_id: ProductUUID.NAMIRASOFT_ERP,
            name: "party",
            tables: [{ table: db.tables.party, required: false }],
            client: {
                getColumns: () => [
                    BaseColumnFormatter.getColumn(db.tables.party.columns.id, new IDFormatter(namirasoft, null)),
                    BaseColumnFormatter.getColumn(db.tables.party.columns.company_id, new IDFormatter(namirasoft, new IStorageLocal())),
                    BaseColumnFormatter.getColumn(db.tables.party.columns.branch_id, new IDFormatter(namirasoft, new IStorageLocal())),
                    BaseColumnFormatter.getColumn(db.tables.party.columns.party_type_id, new IDFormatter(namirasoft, new IStorageLocal())),
                    BaseColumnFormatter.getColumn(db.tables.party.columns.name, new StringFormatter(StringFormatterSizeType.FullName)),
                    BaseColumnFormatter.getColumn(db.tables.party.columns.enabled, new ForeColorFormatter(new BooleanFormatter("Yes", "No"), { true: "green", false: "red" })),
                    BaseColumnFormatter.getColumn(db.tables.party.columns.description, new StringFormatter(StringFormatterSizeType.Description)),
                    BaseColumnFormatter.getColumn(db.tables.party.columns.created_at, new DateTimeFormatter()),
                    BaseColumnFormatter.getColumn(db.tables.party.columns.updated_at, new DateTimeFormatter()),
                ],
                getListURL: () => ErpRoutes.PartyList,
                getNewURL: () => ErpRoutes.PartyNew,
                getViewURL: (id) => ErpRoutes.PartyView(id),
                getEditURL: (id) => ErpRoutes.PartyEdit(id),
                getCopyURL: (id) => ErpRoutes.PartyCopy(id),
            },
            server: {
                value_list: props.servers.erp.value.List,
                list: scopedList(db.tables.party, props.servers.erp.party.List, ["company_id", "branch_id"]),
                get: props.servers.erp.party.Get,
                create: props.servers.erp.party.Create,
                update: props.servers.erp.party.Update,
                delete: props.servers.erp.party.Delete,
            }
        }
    }

    static tag_group(props: NSARouterMakerResult & ErpRouterProps): IEntityInfo<TagGroupRow, TagGroupInputRow>
    {
        let db = new NamirasoftERPMetaDatabase(props.account.token_manager, console.error);
        let namirasoft = new NamirasoftMap(props.account.token_manager, console.error);

        return {
            product_id: ProductUUID.NAMIRASOFT_ERP,
            name: "tag_group",
            tables: [{ table: db.tables.tag_group, required: false }],
            client: {
                getColumns: () => [
                    BaseColumnFormatter.getColumn(db.tables.tag_group.columns.id, new IDFormatter(namirasoft, null)),
                    BaseColumnFormatter.getColumn(db.tables.tag_group.columns.company_id, new IDFormatter(namirasoft, new IStorageLocal())),
                    BaseColumnFormatter.getColumn(db.tables.tag_group.columns.branch_id, new IDFormatter(namirasoft, new IStorageLocal())),
                    BaseColumnFormatter.getColumn(db.tables.tag_group.columns.name, new StringFormatter(StringFormatterSizeType.FullName)),
                    BaseColumnFormatter.getColumn(db.tables.tag_group.columns.enabled, new ForeColorFormatter(new BooleanFormatter("Yes", "No"), { true: "green", false: "red" })),
                    BaseColumnFormatter.getColumn(db.tables.tag_group.columns.description, new StringFormatter(StringFormatterSizeType.Description)),
                    BaseColumnFormatter.getColumn(db.tables.tag_group.columns.created_at, new DateTimeFormatter()),
                    BaseColumnFormatter.getColumn(db.tables.tag_group.columns.updated_at, new DateTimeFormatter()),
                ],
                getListURL: () => ErpRoutes.TagGroupList,
                getNewURL: () => ErpRoutes.TagGroupNew,
                getViewURL: (id) => ErpRoutes.TagGroupView(id),
                getEditURL: (id) => ErpRoutes.TagGroupEdit(id),
                getCopyURL: (id) => ErpRoutes.TagGroupCopy(id),
            },
            server: {
                value_list: props.servers.erp.value.List,
                list: scopedList(db.tables.tag_group, props.servers.erp.tag_group.List, ["company_id", "branch_id"]),
                get: props.servers.erp.tag_group.Get,
                create: props.servers.erp.tag_group.Create,
                update: props.servers.erp.tag_group.Update,
                delete: props.servers.erp.tag_group.Delete,
            }
        }
    }

    static tag(props: NSARouterMakerResult & ErpRouterProps): IEntityInfo<TagRow, TagInputRow>
    {
        let db = new NamirasoftERPMetaDatabase(props.account.token_manager, console.error);
        let namirasoft = new NamirasoftMap(props.account.token_manager, console.error);

        return {
            product_id: ProductUUID.NAMIRASOFT_ERP,
            name: "tag",
            tables: [{ table: db.tables.tag, required: false }],
            client: {
                getColumns: () => [
                    BaseColumnFormatter.getColumn(db.tables.tag.columns.id, new IDFormatter(namirasoft, null)),
                    BaseColumnFormatter.getColumn(db.tables.tag.columns.company_id, new IDFormatter(namirasoft, new IStorageLocal())),
                    BaseColumnFormatter.getColumn(db.tables.tag.columns.branch_id, new IDFormatter(namirasoft, new IStorageLocal())),
                    BaseColumnFormatter.getColumn(db.tables.tag.columns.tag_group_id, new IDFormatter(namirasoft, new IStorageLocal())),
                    BaseColumnFormatter.getColumn(db.tables.tag.columns.name, new StringFormatter(StringFormatterSizeType.FullName)),
                    BaseColumnFormatter.getColumn(db.tables.tag.columns.enabled, new ForeColorFormatter(new BooleanFormatter("Yes", "No"), { true: "green", false: "red" })),
                    BaseColumnFormatter.getColumn(db.tables.tag.columns.description, new StringFormatter(StringFormatterSizeType.Description)),
                    BaseColumnFormatter.getColumn(db.tables.tag.columns.created_at, new DateTimeFormatter()),
                    BaseColumnFormatter.getColumn(db.tables.tag.columns.updated_at, new DateTimeFormatter()),
                ],
                getListURL: () => ErpRoutes.TagList,
                getNewURL: () => ErpRoutes.TagNew,
                getViewURL: (id) => ErpRoutes.TagView(id),
                getEditURL: (id) => ErpRoutes.TagEdit(id),
                getCopyURL: (id) => ErpRoutes.TagCopy(id),
            },
            server: {
                value_list: props.servers.erp.value.List,
                list: scopedList(db.tables.tag, props.servers.erp.tag.List, ["company_id", "branch_id"]),
                get: props.servers.erp.tag.Get,
                create: props.servers.erp.tag.Create,
                update: props.servers.erp.tag.Update,
                delete: props.servers.erp.tag.Delete,
            }
        }
    }

    static lock(props: NSARouterMakerResult & ErpRouterProps): IEntityInfo<LockRow, LockInputRow>
    {
        let db = new NamirasoftERPMetaDatabase(props.account.token_manager, console.error);
        let namirasoft = new NamirasoftMap(props.account.token_manager, console.error);

        return {
            product_id: ProductUUID.NAMIRASOFT_ERP,
            name: "lock",
            tables: [{ table: db.tables.lock, required: false }],
            client: {
                getColumns: () => [
                    BaseColumnFormatter.getColumn(db.tables.lock.columns.id, new IDFormatter(namirasoft, null)),
                    BaseColumnFormatter.getColumn(db.tables.lock.columns.company_id, new IDFormatter(namirasoft, new IStorageLocal())),
                    BaseColumnFormatter.getColumn(db.tables.lock.columns.branch_id, new IDFormatter(namirasoft, new IStorageLocal())),
                    BaseColumnFormatter.getColumn(db.tables.lock.columns.account_id, new IDFormatter(namirasoft, new IStorageLocal())),
                    BaseColumnFormatter.getColumn(db.tables.lock.columns.party_type_id, new IDFormatter(namirasoft, new IStorageLocal())),
                    BaseColumnFormatter.getColumn(db.tables.lock.columns.party_id, new IDFormatter(namirasoft, new IStorageLocal())),
                    BaseColumnFormatter.getColumn(db.tables.lock.columns.to_date_time, new DateTimeFormatter()),
                    BaseColumnFormatter.getColumn(db.tables.lock.columns.enabled, new ForeColorFormatter(new BooleanFormatter("Yes", "No"), { true: "green", false: "red" })),
                    BaseColumnFormatter.getColumn(db.tables.lock.columns.description, new StringFormatter(StringFormatterSizeType.Description)),
                    BaseColumnFormatter.getColumn(db.tables.lock.columns.created_at, new DateTimeFormatter()),
                    BaseColumnFormatter.getColumn(db.tables.lock.columns.updated_at, new DateTimeFormatter()),
                ],
                getListURL: () => ErpRoutes.LockList,
                getNewURL: () => ErpRoutes.LockNew,
                getViewURL: (id) => ErpRoutes.LockView(id),
                getEditURL: (id) => ErpRoutes.LockEdit(id),
                getCopyURL: (id) => ErpRoutes.LockCopy(id),
            },
            server: {
                value_list: props.servers.erp.value.List,
                list: scopedList(db.tables.lock, props.servers.erp.lock.List, ["company_id", "branch_id"]),
                get: props.servers.erp.lock.Get,
                create: props.servers.erp.lock.Create,
                update: props.servers.erp.lock.Update,
                delete: props.servers.erp.lock.Delete,
            }
        }
    }

    static vehicle(props: NSARouterMakerResult & ErpRouterProps): IEntityInfo<VehicleRow, VehicleInputRow>
    {
        let db = new NamirasoftERPMetaDatabase(props.account.token_manager, console.error);
        let namirasoft = new NamirasoftMap(props.account.token_manager, console.error);

        return {
            product_id: ProductUUID.NAMIRASOFT_ERP,
            name: "vehicle",
            tables: [{ table: db.tables.vehicle, required: false }],
            client: {
                getColumns: () => [
                    BaseColumnFormatter.getColumn(db.tables.vehicle.columns.id, new IDFormatter(namirasoft, null)),
                    BaseColumnFormatter.getColumn(db.tables.vehicle.columns.company_id, new IDFormatter(namirasoft, new IStorageLocal())),
                    BaseColumnFormatter.getColumn(db.tables.vehicle.columns.branch_id, new IDFormatter(namirasoft, new IStorageLocal())),
                    BaseColumnFormatter.getColumn(db.tables.vehicle.columns.fuel_id, new IDFormatter(namirasoft, new IStorageLocal())),
                    BaseColumnFormatter.getColumn(db.tables.vehicle.columns.name, new StringFormatter(StringFormatterSizeType.FullName)),
                    BaseColumnFormatter.getColumn(db.tables.vehicle.columns.license_plate, new StringFormatter(StringFormatterSizeType.Word)),
                    BaseColumnFormatter.getColumn(db.tables.vehicle.columns.vin, new StringFormatter(StringFormatterSizeType.Word)),
                    BaseColumnFormatter.getColumn(db.tables.vehicle.columns.model_year, new IntegerFormatter(StringFormatterSizeType.Word)),
                    BaseColumnFormatter.getColumn(db.tables.vehicle.columns.color, new StringFormatter(StringFormatterSizeType.Word)),
                    BaseColumnFormatter.getColumn(db.tables.vehicle.columns.odometer_unit, new EnumFormatter()),
                    BaseColumnFormatter.getColumn(db.tables.vehicle.columns.tank_capacity_value, new IntegerFormatter(StringFormatterSizeType.Word)),
                    BaseColumnFormatter.getColumn(db.tables.vehicle.columns.tank_capacity_unit, new EnumFormatter()),
                    BaseColumnFormatter.getColumn(db.tables.vehicle.columns.description, new StringFormatter(StringFormatterSizeType.Description)),
                    BaseColumnFormatter.getColumn(db.tables.vehicle.columns.created_at, new DateTimeFormatter()),
                    BaseColumnFormatter.getColumn(db.tables.vehicle.columns.updated_at, new DateTimeFormatter()),
                ],
                getListURL: () => ErpRoutes.VehicleList,
                getNewURL: () => ErpRoutes.VehicleNew,
                getViewURL: (id) => ErpRoutes.VehicleView(id),
                getEditURL: (id) => ErpRoutes.VehicleEdit(id),
                getCopyURL: (id) => ErpRoutes.VehicleCopy(id),
            },
            server: {
                value_list: props.servers.erp.value.List,
                list: scopedList(db.tables.vehicle, props.servers.erp.vehicle.List, ["company_id", "branch_id"]),
                get: props.servers.erp.vehicle.Get,
                create: props.servers.erp.vehicle.Create,
                update: props.servers.erp.vehicle.Update,
                delete: props.servers.erp.vehicle.Delete,
            }
        }
    }

    static service_type(props: NSARouterMakerResult & ErpRouterProps): IEntityInfo<ServiceTypeRow, ServiceTypeInputRow>
    {
        let db = new NamirasoftERPMetaDatabase(props.account.token_manager, console.error);
        let namirasoft = new NamirasoftMap(props.account.token_manager, console.error);

        return {
            product_id: ProductUUID.NAMIRASOFT_ERP,
            name: "service_type",
            tables: [{ table: db.tables.service_type, required: false }],
            client: {
                getColumns: () => [
                    BaseColumnFormatter.getColumn(db.tables.service_type.columns.id, new IDFormatter(namirasoft, null)),
                    BaseColumnFormatter.getColumn(db.tables.service_type.columns.company_id, new IDFormatter(namirasoft, new IStorageLocal())),
                    BaseColumnFormatter.getColumn(db.tables.service_type.columns.branch_id, new IDFormatter(namirasoft, new IStorageLocal())),
                    BaseColumnFormatter.getColumn(db.tables.service_type.columns.name, new StringFormatter(StringFormatterSizeType.FullName)),
                    BaseColumnFormatter.getColumn(db.tables.service_type.columns.description, new StringFormatter(StringFormatterSizeType.Description)),
                    BaseColumnFormatter.getColumn(db.tables.service_type.columns.created_at, new DateTimeFormatter()),
                    BaseColumnFormatter.getColumn(db.tables.service_type.columns.updated_at, new DateTimeFormatter()),
                ],
                getListURL: () => ErpRoutes.ServiceTypeList,
                getNewURL: () => ErpRoutes.ServiceTypeNew,
                getViewURL: (id) => ErpRoutes.ServiceTypeView(id),
                getEditURL: (id) => ErpRoutes.ServiceTypeEdit(id),
                getCopyURL: (id) => ErpRoutes.ServiceTypeCopy(id),
            },
            server: {
                value_list: props.servers.erp.value.List,
                list: scopedList(db.tables.service_type, props.servers.erp.service_type.List, ["company_id", "branch_id"]),
                get: props.servers.erp.service_type.Get,
                create: props.servers.erp.service_type.Create,
                update: props.servers.erp.service_type.Update,
                delete: props.servers.erp.service_type.Delete,
            }
        }
    }

    static maintenance_plan(props: NSARouterMakerResult & ErpRouterProps): IEntityInfo<MaintenancePlanRow, MaintenancePlanInputRow>
    {
        let db = new NamirasoftERPMetaDatabase(props.account.token_manager, console.error);
        let namirasoft = new NamirasoftMap(props.account.token_manager, console.error);

        return {
            product_id: ProductUUID.NAMIRASOFT_ERP,
            name: "maintenance_plan",
            tables: [{ table: db.tables.maintenance_plan, required: false }],
            client: {
                getColumns: () => [
                    BaseColumnFormatter.getColumn(db.tables.maintenance_plan.columns.id, new IDFormatter(namirasoft, null)),
                    BaseColumnFormatter.getColumn(db.tables.maintenance_plan.columns.company_id, new IDFormatter(namirasoft, new IStorageLocal())),
                    BaseColumnFormatter.getColumn(db.tables.maintenance_plan.columns.branch_id, new IDFormatter(namirasoft, new IStorageLocal())),
                    BaseColumnFormatter.getColumn(db.tables.maintenance_plan.columns.vehicle_id, new IDFormatter(namirasoft, new IStorageLocal())),
                    BaseColumnFormatter.getColumn(db.tables.maintenance_plan.columns.service_type_id, new IDFormatter(namirasoft, new IStorageLocal())),
                    BaseColumnFormatter.getColumn(db.tables.maintenance_plan.columns.topic_id, new IDFormatter(namirasoft, new IStorageLocal())),
                    BaseColumnFormatter.getColumn(db.tables.maintenance_plan.columns.starts_at, new DateTimeFormatter()),
                    BaseColumnFormatter.getColumn(db.tables.maintenance_plan.columns.every_distance_value, new HiddenWhenZeroFormatter(new IntegerFormatter(StringFormatterSizeType.Word), "every_distance_value")),
                    BaseColumnFormatter.getColumn(db.tables.maintenance_plan.columns.every_distance_unit, new HiddenWhenZeroFormatter(new EnumFormatter(), "every_distance_value")),
                    BaseColumnFormatter.getColumn(db.tables.maintenance_plan.columns.every_time_value, new HiddenWhenZeroFormatter(new IntegerFormatter(StringFormatterSizeType.Word), "every_time_value")),
                    BaseColumnFormatter.getColumn(db.tables.maintenance_plan.columns.every_time_unit, new HiddenWhenZeroFormatter(new EnumFormatter(), "every_time_value")),
                    BaseColumnFormatter.getColumn(db.tables.maintenance_plan.columns.status, new EnumFormatter()),
                    BaseColumnFormatter.getColumn(db.tables.maintenance_plan.columns.overdue_time_at, new DateTimeFormatter()),
                    BaseColumnFormatter.getColumn(db.tables.maintenance_plan.columns.overdue_odometer_value, new IntegerFormatter(StringFormatterSizeType.Word)),
                    BaseColumnFormatter.getColumn(db.tables.maintenance_plan.columns.remaining_days, new IntegerFormatter(StringFormatterSizeType.Word)),
                    BaseColumnFormatter.getColumn(db.tables.maintenance_plan.columns.remaining_odometer_value, new IntegerFormatter(StringFormatterSizeType.Word)),
                    BaseColumnFormatter.getColumn(db.tables.maintenance_plan.columns.description, new StringFormatter(StringFormatterSizeType.Description)),
                    BaseColumnFormatter.getColumn(db.tables.maintenance_plan.columns.created_at, new DateTimeFormatter()),
                    BaseColumnFormatter.getColumn(db.tables.maintenance_plan.columns.updated_at, new DateTimeFormatter()),
                ],
                getListURL: () => ErpRoutes.MaintenancePlanList,
                getNewURL: () => ErpRoutes.MaintenancePlanNew,
                getViewURL: (id) => ErpRoutes.MaintenancePlanView(id),
                getEditURL: (id) => ErpRoutes.MaintenancePlanEdit(id),
                getCopyURL: (id) => ErpRoutes.MaintenancePlanCopy(id),
            },
            server: {
                value_list: props.servers.erp.value.List,
                list: scopedList(db.tables.maintenance_plan, props.servers.erp.maintenance_plan.List, ["company_id", "branch_id"]),
                get: props.servers.erp.maintenance_plan.Get,
                create: props.servers.erp.maintenance_plan.Create,
                update: props.servers.erp.maintenance_plan.Update,
                delete: props.servers.erp.maintenance_plan.Delete,
            }
        }
    }

    static service(props: NSARouterMakerResult & ErpRouterProps): IEntityInfo<ServiceRow, ServiceInputRow>
    {
        let db = new NamirasoftERPMetaDatabase(props.account.token_manager, console.error);
        let namirasoft = new NamirasoftMap(props.account.token_manager, console.error);

        return {
            product_id: ProductUUID.NAMIRASOFT_ERP,
            name: "service",
            tables: [{ table: db.tables.service, required: false }],
            client: {
                getColumns: () => [
                    BaseColumnFormatter.getColumn(db.tables.service.columns.id, new IDFormatter(namirasoft, null)),
                    BaseColumnFormatter.getColumn(db.tables.service.columns.company_id, new IDFormatter(namirasoft, new IStorageLocal())),
                    BaseColumnFormatter.getColumn(db.tables.service.columns.branch_id, new IDFormatter(namirasoft, new IStorageLocal())),
                    BaseColumnFormatter.getColumn(db.tables.service.columns.service_type_id, new IDFormatter(namirasoft, new IStorageLocal())),
                    BaseColumnFormatter.getColumn(db.tables.service.columns.vehicle_id, new IDFormatter(namirasoft, new IStorageLocal())),
                    BaseColumnFormatter.getColumn(db.tables.service.columns.datetime, new DateTimeFormatter()),
                    BaseColumnFormatter.getColumn(db.tables.service.columns.description, new StringFormatter(StringFormatterSizeType.Description)),
                    BaseColumnFormatter.getColumn(db.tables.service.columns.odometer_value, new IntegerFormatter(StringFormatterSizeType.Word)),
                    BaseColumnFormatter.getColumn(db.tables.service.columns.odometer_unit, new EnumFormatter()),
                    BaseColumnFormatter.getColumn(db.tables.service.columns.created_at, new DateTimeFormatter()),
                    BaseColumnFormatter.getColumn(db.tables.service.columns.updated_at, new DateTimeFormatter()),
                ],
                getListURL: () => ErpRoutes.ServiceList,
                getNewURL: () => ErpRoutes.ServiceNew,
                getViewURL: (id) => ErpRoutes.ServiceView(id),
                getEditURL: (id) => ErpRoutes.ServiceEdit(id),
                getCopyURL: (id) => ErpRoutes.ServiceCopy(id),
            },
            server: {
                value_list: props.servers.erp.value.List,
                list: scopedList(db.tables.service, props.servers.erp.service.List, ["company_id", "branch_id"]),
                get: props.servers.erp.service.Get,
                create: props.servers.erp.service.Create,
                update: props.servers.erp.service.Update,
                delete: props.servers.erp.service.Delete,
            }
        }
    }

    static odometer(props: NSARouterMakerResult & ErpRouterProps): IEntityInfo<OdometerRow, OdometerInputRow>
    {
        let db = new NamirasoftERPMetaDatabase(props.account.token_manager, console.error);
        let namirasoft = new NamirasoftMap(props.account.token_manager, console.error);

        return {
            product_id: ProductUUID.NAMIRASOFT_ERP,
            name: "odometer",
            tables: [{ table: db.tables.odometer, required: false }],
            client: {
                getColumns: () => [
                    BaseColumnFormatter.getColumn(db.tables.odometer.columns.id, new IDFormatter(namirasoft, null)),
                    BaseColumnFormatter.getColumn(db.tables.odometer.columns.company_id, new IDFormatter(namirasoft, new IStorageLocal())),
                    BaseColumnFormatter.getColumn(db.tables.odometer.columns.branch_id, new IDFormatter(namirasoft, new IStorageLocal())),
                    BaseColumnFormatter.getColumn(db.tables.odometer.columns.vehicle_id, new IDFormatter(namirasoft, new IStorageLocal())),
                    BaseColumnFormatter.getColumn(db.tables.odometer.columns.name, new StringFormatter(StringFormatterSizeType.FullName)),
                    BaseColumnFormatter.getColumn(db.tables.odometer.columns.date, new DateTimeFormatter()),
                    BaseColumnFormatter.getColumn(db.tables.odometer.columns.value, new IntegerFormatter(StringFormatterSizeType.Word)),
                    BaseColumnFormatter.getColumn(db.tables.odometer.columns.unit, new EnumFormatter()),
                    BaseColumnFormatter.getColumn(db.tables.odometer.columns.description, new StringFormatter(StringFormatterSizeType.Description)),
                    BaseColumnFormatter.getColumn(db.tables.odometer.columns.created_at, new DateTimeFormatter()),
                    BaseColumnFormatter.getColumn(db.tables.odometer.columns.updated_at, new DateTimeFormatter()),
                ],
                getListURL: () => ErpRoutes.OdometerList,
                getNewURL: () => ErpRoutes.OdometerNew,
                getViewURL: (id) => ErpRoutes.OdometerView(id),
                getEditURL: (id) => ErpRoutes.OdometerEdit(id),
                getCopyURL: (id) => ErpRoutes.OdometerCopy(id),
            },
            server: {
                value_list: props.servers.erp.value.List,
                list: scopedList(db.tables.odometer, props.servers.erp.odometer.List, ["company_id", "branch_id"]),
                get: props.servers.erp.odometer.Get,
                create: props.servers.erp.odometer.Create,
                update: props.servers.erp.odometer.Update,
                delete: props.servers.erp.odometer.Delete,
            }
        }
    }

    static fuel(props: NSARouterMakerResult & ErpRouterProps): IEntityInfo<FuelRow, FuelInputRow>
    {
        let db = new NamirasoftERPMetaDatabase(props.account.token_manager, console.error);
        let namirasoft = new NamirasoftMap(props.account.token_manager, console.error);

        return {
            product_id: ProductUUID.NAMIRASOFT_ERP,
            name: "fuel",
            tables: [{ table: db.tables.fuel, required: false }],
            client: {
                getColumns: () => [
                    BaseColumnFormatter.getColumn(db.tables.fuel.columns.id, new IDFormatter(namirasoft, null)),
                    BaseColumnFormatter.getColumn(db.tables.fuel.columns.company_id, new IDFormatter(namirasoft, new IStorageLocal())),
                    BaseColumnFormatter.getColumn(db.tables.fuel.columns.branch_id, new IDFormatter(namirasoft, new IStorageLocal())),
                    BaseColumnFormatter.getColumn(db.tables.fuel.columns.name, new StringFormatter(StringFormatterSizeType.FullName)),
                    BaseColumnFormatter.getColumn(db.tables.fuel.columns.description, new StringFormatter(StringFormatterSizeType.Description)),
                    BaseColumnFormatter.getColumn(db.tables.fuel.columns.created_at, new DateTimeFormatter()),
                    BaseColumnFormatter.getColumn(db.tables.fuel.columns.updated_at, new DateTimeFormatter()),
                ],
                getListURL: () => ErpRoutes.FuelList,
                getNewURL: () => ErpRoutes.FuelNew,
                getViewURL: (id) => ErpRoutes.FuelView(id),
                getEditURL: (id) => ErpRoutes.FuelEdit(id),
                getCopyURL: (id) => ErpRoutes.FuelCopy(id),
            },
            server: {
                value_list: props.servers.erp.value.List,
                list: scopedList(db.tables.fuel, props.servers.erp.fuel.List, ["company_id", "branch_id"]),
                get: props.servers.erp.fuel.Get,
                create: props.servers.erp.fuel.Create,
                update: props.servers.erp.fuel.Update,
                delete: props.servers.erp.fuel.Delete,
            }
        }
    }

    static refueling(props: NSARouterMakerResult & ErpRouterProps): IEntityInfo<RefuelingRow, RefuelingInputRow>
    {
        let db = new NamirasoftERPMetaDatabase(props.account.token_manager, console.error);
        let namirasoft = new NamirasoftMap(props.account.token_manager, console.error);

        return {
            product_id: ProductUUID.NAMIRASOFT_ERP,
            name: "refueling",
            tables: [{ table: db.tables.refueling, required: false }],
            client: {
                getColumns: () => [
                    BaseColumnFormatter.getColumn(db.tables.refueling.columns.id, new IDFormatter(namirasoft, null)),
                    BaseColumnFormatter.getColumn(db.tables.refueling.columns.company_id, new IDFormatter(namirasoft, new IStorageLocal())),
                    BaseColumnFormatter.getColumn(db.tables.refueling.columns.branch_id, new IDFormatter(namirasoft, new IStorageLocal())),
                    BaseColumnFormatter.getColumn(db.tables.refueling.columns.vehicle_id, new IDFormatter(namirasoft, new IStorageLocal())),
                    BaseColumnFormatter.getColumn(db.tables.refueling.columns.fuel_id, new IDFormatter(namirasoft, new IStorageLocal())),
                    BaseColumnFormatter.getColumn(db.tables.refueling.columns.datetime, new DateTimeFormatter()),
                    BaseColumnFormatter.getColumn(db.tables.refueling.columns.quantity, new IntegerFormatter(StringFormatterSizeType.Word)),
                    BaseColumnFormatter.getColumn(db.tables.refueling.columns.unit, new EnumFormatter()),
                    BaseColumnFormatter.getColumn(db.tables.refueling.columns.is_full_tank, new ForeColorFormatter(new BooleanFormatter("Yes", "No"), { true: "green", false: "red" })),
                    BaseColumnFormatter.getColumn(db.tables.refueling.columns.description, new StringFormatter(StringFormatterSizeType.Description)),
                    BaseColumnFormatter.getColumn(db.tables.refueling.columns.odometer_value, new IntegerFormatter(StringFormatterSizeType.Word)),
                    BaseColumnFormatter.getColumn(db.tables.refueling.columns.odometer_unit, new EnumFormatter()),
                    BaseColumnFormatter.getColumn(db.tables.refueling.columns.created_at, new DateTimeFormatter()),
                    BaseColumnFormatter.getColumn(db.tables.refueling.columns.updated_at, new DateTimeFormatter()),
                ],
                getListURL: () => ErpRoutes.RefuelingList,
                getNewURL: () => ErpRoutes.RefuelingNew,
                getViewURL: (id) => ErpRoutes.RefuelingView(id),
                getEditURL: (id) => ErpRoutes.RefuelingEdit(id),
                getCopyURL: (id) => ErpRoutes.RefuelingCopy(id),
            },
            server: {
                value_list: props.servers.erp.value.List,
                list: scopedList(db.tables.refueling, props.servers.erp.refueling.List, ["company_id", "branch_id"]),
                get: props.servers.erp.refueling.Get,
                create: props.servers.erp.refueling.Create,
                update: props.servers.erp.refueling.Update,
                delete: props.servers.erp.refueling.Delete,
            }
        }
    }

    static gifi(props: NSARouterMakerResult & ErpRouterProps): IEntityInfo<GifiRow, any>
    {
        let db = new NamirasoftERPMetaDatabase(props.account.token_manager, console.error);

        return {
            product_id: ProductUUID.NAMIRASOFT_ERP,
            name: "gifi",
            tables: [{ table: db.tables.gifi, required: false }],
            client: {
                getColumns: () => [
                    BaseColumnFormatter.getColumn(db.tables.gifi.columns.id, new StringFormatter(StringFormatterSizeType.Word)),
                    BaseColumnFormatter.getColumn(db.tables.gifi.columns.name, new StringFormatter(StringFormatterSizeType.FullName)),
                    BaseColumnFormatter.getColumn(db.tables.gifi.columns.statement_type, new EnumFormatter()),
                    BaseColumnFormatter.getColumn(db.tables.gifi.columns.category, new StringFormatter(StringFormatterSizeType.Word)),
                    BaseColumnFormatter.getColumn(db.tables.gifi.columns.subcategory, new StringFormatter(StringFormatterSizeType.Word)),
                    BaseColumnFormatter.getColumn(db.tables.gifi.columns.parent_id, new StringFormatter(StringFormatterSizeType.Word)),
                    BaseColumnFormatter.getColumn(db.tables.gifi.columns.description, new StringFormatter(StringFormatterSizeType.Description)),
                    BaseColumnFormatter.getColumn(db.tables.gifi.columns.is_total, new ForeColorFormatter(new BooleanFormatter("Yes", "No"), { true: "green" })),
                    BaseColumnFormatter.getColumn(db.tables.gifi.columns.applicable_to_corporation, new BooleanFormatter("Yes", "No")),
                    BaseColumnFormatter.getColumn(db.tables.gifi.columns.applicable_to_partnership, new BooleanFormatter("Yes", "No")),
                    BaseColumnFormatter.getColumn(db.tables.gifi.columns.active, new ForeColorFormatter(new BooleanFormatter("Yes", "No"), { true: "green", false: "red" })),
                    BaseColumnFormatter.getColumn(db.tables.gifi.columns.source_version, new StringFormatter(StringFormatterSizeType.Word)),
                ],
                getListURL: () => ErpRoutes.GifiList,
                getNewURL: null,
                getViewURL: (code) => ErpRoutes.GifiView(code),
                getEditURL: (code) => ErpRoutes.GifiView(code),
                getCopyURL: (code) => ErpRoutes.GifiView(code),
            },
            server: {
                value_list: props.servers.erp.value.List,
                list: props.servers.erp.gifi.List,
                get: props.servers.erp.gifi.Get,
                create: async () => { throw new Error("GIFI reference data is read-only."); },
                update: async () => { throw new Error("GIFI reference data is read-only."); },
                delete: async () => { throw new Error("GIFI reference data is read-only."); },
            }
        }
    }

    static journal_entry(props: NSARouterMakerResult & ErpRouterProps): IEntityInfo<JournalEntryRow, JournalEntryInputRow>
    {
        let db = new NamirasoftERPMetaDatabase(props.account.token_manager, console.error);
        let namirasoft = new NamirasoftMap(props.account.token_manager, console.error);

        return {
            product_id: ProductUUID.NAMIRASOFT_ERP,
            name: "journal_entry",
            tables: [{ table: db.tables.journal_entry, required: false }],
            client: {
                getColumns: () => [
                    BaseColumnFormatter.getColumn(db.tables.journal_entry.columns.id, new IDFormatter(namirasoft, null)),
                    BaseColumnFormatter.getColumn(db.tables.journal_entry.columns.company_id, new IDFormatter(namirasoft, new IStorageLocal())),
                    BaseColumnFormatter.getColumn(db.tables.journal_entry.columns.branch_id, new IDFormatter(namirasoft, new IStorageLocal())),
                    BaseColumnFormatter.getColumn(db.tables.journal_entry.columns.fiscal_year_id, new IDFormatter(namirasoft, new IStorageLocal())),
                    BaseColumnFormatter.getColumn(db.tables.journal_entry.columns.date_time, new DateTimeFormatter()),
                    BaseColumnFormatter.getColumn(db.tables.journal_entry.columns.reference_number, new StringFormatter(StringFormatterSizeType.Word)),
                    BaseColumnFormatter.getColumn(db.tables.journal_entry.columns.reference_date_time, new DateTimeFormatter()),
                    BaseColumnFormatter.getColumn(db.tables.journal_entry.columns.type, new ForeColorFormatter(new EnumFormatter(), { [JournalEntryType.Opening]: "red", [JournalEntryType.Closing]: "red" })),
                    BaseColumnFormatter.getColumn(db.tables.journal_entry.columns.total_debit, new FloatFormatter()),
                    BaseColumnFormatter.getColumn(db.tables.journal_entry.columns.total_credit, new FloatFormatter()),
                    BaseColumnFormatter.getColumn(db.tables.journal_entry.columns.finalized, new ForeColorFormatter(new BooleanFormatter("Yes", "No"), { true: "green", false: "yellow" })),
                    BaseColumnFormatter.getColumn(db.tables.journal_entry.columns.enabled, new ForeColorFormatter(new BooleanFormatter("Yes", "No"), { true: "green", false: "red" })),
                    BaseColumnFormatter.getColumn(db.tables.journal_entry.columns.description, new StringFormatter(StringFormatterSizeType.Description)),
                    BaseColumnFormatter.getColumn(db.tables.journal_entry.columns.created_at, new DateTimeFormatter()),
                    BaseColumnFormatter.getColumn(db.tables.journal_entry.columns.updated_at, new DateTimeFormatter()),
                ],
                getListURL: () => ErpRoutes.JournalEntryList,
                getNewURL: () => ErpRoutes.JournalEntryNew,
                getViewURL: (id) => ErpRoutes.JournalEntryView(id),
                getEditURL: (id) => ErpRoutes.JournalEntryEdit(id),
                getCopyURL: (id) => ErpRoutes.JournalEntryCopy(id),
                getMenus: (getIDs, getRows) =>
                {
                    let reload = () => window.location.reload();
                    return {
                        "Finalize": [
                            JournalEntryMenus.setFinalized(props.servers.erp, getIDs, getRows, reload),
                            JournalEntryMenus.unsetFinalized(props.servers.erp, getIDs, getRows, reload),
                        ]
                    };
                },
            },
            server: {
                value_list: props.servers.erp.value.List,
                list: scopedList(db.tables.journal_entry, props.servers.erp.journal_entry.List, ["company_id", "branch_id", "fiscal_year_id"]),
                get: props.servers.erp.journal_entry.Get,
                create: props.servers.erp.journal_entry.Create,
                update: props.servers.erp.journal_entry.Update,
                delete: props.servers.erp.journal_entry.Delete,
            }
        }
    }
}